<template>
	<div class="wrapper-div">
		<div class="content-div mb-5 px-6">
			<duty-timeline v-if="selectedDuty" :duty="selectedDuty" />
		</div>

		<v-expansion-panels v-model="openedPanels" elevation="0" multiple>
			<div v-for="(group, index) in dutyActivityGroups" :key="group.block ?? 'group-' + index" class="w-100">
				<div v-if="isBreakGroup(group)" class="px-6">
					<DutyBreaksCard class="mx-6" :breaksData="group" />
				</div>

				<div v-else class="content-div">
					<v-expansion-panel bg-color="#FAFAFA" :value="'panel-' + index">
						<v-expansion-panel-title class="timeline-panel-text">
							<div class="w-100">
								<div class="text-body-2 font-weight-medium">{{ formatLabel(group.label) }}</div>

								<div class="d-flex align-center mt-2 text-medium-emphasis group-stats-row">
									<div class="d-flex align-center group-stat">
										<v-icon size="10" class="mr-1"> mdi-timer-sand </v-icon>
										<span class="text-caption">
											{{ getDuration(group.activities) }}
										</span>
									</div>

									<div class="d-flex align-center group-stat">
										<v-icon size="10" class="mr-1"> mdi-clock-outline </v-icon>
										<span class="text-caption">
											{{ group.activities[0].startTime }} -
											{{ group.activities[group.activities.length - 1].endTime }}
										</span>
									</div>

									<div class="d-flex align-center group-stat">
										<v-icon size="10" class="mr-1"> mdi-chart-timeline-variant </v-icon>
										<span class="text-caption"> {{ group.activities.length }} {{ $t('activities') }} </span>
									</div>
								</div>
							</div>
						</v-expansion-panel-title>

						<v-expansion-panel-text>
							<v-timeline truncate-line="both" align="start" density="compact" hide-opposite>
								<v-timeline-item
									class="custom-timeline-item"
									size="x-small"
									v-for="activity in group.activities"
									:key="'act' + activity.sort"
									:fill-dot="true"
									:dot-color="getColor(activity)"
									:icon="getIcon(activity)"
								>
									<div class="d-flex activity-row">
										<div class="activity-time">
											{{ activity.startTime + ' - ' + activity.endTime }}
										</div>
										<div class="activity-content">
											<strong>
												{{ activity.activity }}
											</strong>
											<br v-if="activity.line || activity.course || (activity.block && duty_block)" />
											<span v-if="activity.line" class="text-style">{{ $t('duty_line') + ' ' + activity.line }}</span>
											<span v-if="activity.line && activity.course" class="text-caption">, </span>
											<span v-if="activity.course" class="text-style">{{ $t('duty_lineCourse') + ' ' + activity.course }}</span>
											<span v-if="(activity.line || activity.course) && activity.block && duty_block" class="text-style">, </span>
											<span v-if="activity.block && duty_block" class="text-style">{{ $t('duty_block') + ' ' + activity.block }}</span>
											<br v-if="activity.line || activity.course || (activity.block && duty_block)" />
											<div class="text-style">
												<span>{{ activity.startStop }}</span>
												<span v-if="activity.startStop && activity.endStop">{{ ' - ' }}</span>
												<span>{{ activity.endStop }}</span>
											</div>
										</div>
									</div>
								</v-timeline-item>
							</v-timeline>
						</v-expansion-panel-text>
					</v-expansion-panel>
				</div>

				<v-divider v-if="index !== dutyActivityGroups.length - 1" vertical style="height: 20px" class="ml-10 border-opacity-100" color="#eeeeee" />
			</div>
		</v-expansion-panels>
	</div>
</template>

<script lang="ts" setup>
import { onBeforeMount, ref } from 'vue';
import DutyTimeline from './DutyTimeline.vue';
import DutyBreaksCard from './DutyBreaksCard.vue';
import { useDutyStore } from '@/rennovation/planning-view/store';
import { usePlanningBridgeStore } from '@/rennovation/planning-view/store/bridge/planningBridge';
import { ApiDuty, DutyActivity, DutyActivityGroup } from '@/rennovation/planning-view/types/api/duties-response';
import useDateTime from '../../../../../../../components/composables/useDateTime';

const dutyStore = useDutyStore();
const planningBridge = usePlanningBridgeStore();
const { formatSecondsDurationToHM, formatTimeToSeconds } = useDateTime();

const selectedDuty = ref<ApiDuty | null>(null);
// API now returns activities pre-grouped: [{ block, label, activities: DutyActivity[] }, ...]
// Break groups arrive as their own entry with a single activity where activity.break === 1
const dutyActivityGroups = ref<DutyActivityGroup[]>([]);
const openedPanels = ref<string[]>([]);

const props = withDefaults(
	defineProps<{
		dutyID: string;
	}>(),
	{}
);

defineOptions({
	inheritAttrs: false,
});

const isBreakGroup = (group: DutyActivityGroup): boolean => {
	return !!group.activities?.[0]?.break;
};

const formatLabel = (label: string | null): string => {
	const defaultLabel = 'Umlauf';
	return label || defaultLabel;
};

const getIcon = (activity: DutyActivity): string => {
	if (activity.break) {
		return 'mdi-pause-circle-outline';
	} else if (activity.driving) {
		return 'mdi-steering';
	} else {
		return '';
	}
};

const getColor = (activity: DutyActivity): string => {
	if (activity.break) {
		return 'error';
	} else if (activity.driving) {
		return 'success';
	} else {
		return 'amber';
	}
};

const getDuration = (activities: DutyActivity[]): string => {
	const lastIndex = activities.length - 1;
	const start = activities[lastIndex]?.endTime;
	const end = activities[0]?.startTime;

	return formatSecondsDurationToHM(formatTimeToSeconds(start) - formatTimeToSeconds(end));
};

onBeforeMount(async () => {
	const [dutyResponse, dutyActivitiesRes] = await Promise.all([dutyStore.fetchDutyByID(props.dutyID), dutyStore.loadDutyActivities(props.dutyID)]);

	selectedDuty.value = dutyResponse;
	dutyActivityGroups.value = dutyActivitiesRes;

	openedPanels.value = dutyActivitiesRes.map((group, index) => (!isBreakGroup(group) ? `panel-${index}` : null)).filter((val): val is string => val !== null);
});
</script>

<style scoped>
.wrapper-div {
	max-height: 600px;
	width: 100%;
	overflow-y: auto;
	margin-top: 20px;
	margin-bottom: 20px;
}

.content-div {
	background-color: #fafafa;
	margin-left: 24px;
	margin-right: 24px;
	border: 1px solid #eeeeee;
	border-radius: 8px;
	overflow: hidden;
}

:deep(.timeline-panel-text) {
	border-bottom: 1px solid #eeeeee;
	padding: 16px !important;
}

:deep(.custom-timeline-item .v-timeline-divider__dot) {
	width: 20px;
	height: 20px;
}

:deep(.custom-timeline-item .v-timeline-divider__inner-dot) {
	width: 20px;
	height: 20px;
}

:deep(.custom-timeline-item .v-timeline-item__body) {
	padding-left: 16px !important;
}

.activity-row {
	gap: 12px;
	flex-wrap: nowrap;
}

.activity-time {
	font-family: Roboto, sans-serif;
	flex: 0 0 auto;
	width: 74px;
	white-space: nowrap;
	font-size: 14px;
	line-height: 150%;
	font-weight: 400;
	color: #757575;
}

.activity-content {
	font-family: Roboto, sans-serif;
	flex: 1 1 auto;
	min-width: 0;
	overflow-wrap: normal;
	word-break: normal;
	font-size: 14px;
	line-height: 150%;
	font-weight: 400;
	padding-left: 24px;
}
.text-style {
	font-family: Roboto, sans-serif;
	font-size: 12px;
	line-height: 150%;
	font-weight: 400;
	color: #757575;
}
.group-stats-row {
	flex-wrap: nowrap;
	column-gap: 10px;
	overflow-x: auto;
	scrollbar-width: none;
	-ms-overflow-style: none;
}
.group-stats-row::-webkit-scrollbar {
	display: none;
}
.group-stat {
	white-space: nowrap !important;
	flex: 0 0 auto;
}

.group-stat .text-caption {
	font-size: 0.6875rem;
	letter-spacing: 0 !important;
	line-height: 1.2;
}
</style>
